import { z } from 'zod';

import { AgentIdSchema } from '../agent/agent';
import { CardColumnIdSchema, TaskColumnIdSchema } from '../board_config/org_board_config';
import { SquadIdSchema } from '../project/project';
import { TaskIdSchema } from '../task/task_id';
import { TaskWithCardSchema } from '../task/task_with_card';
import { TriageCardRowSchema } from '../triage/triage_card_row';

import { ConversationBoardCardSchema } from './card_board';
import { CardIdSchema } from './card_id';

/**
 * One delta the server pushes over SSE. `*_upsert` carries the full board DTO
 * (no client refetch); `*_remove` tells the client to drop the card/task from
 * the squad board (closed / archived / reassigned out of scope). Column enums
 * are reused from the board config so the push shares the board's contract.
 */
export const CardPushItemSchema = z.discriminatedUnion('kind', [
  z.object({
    kind: z.literal('conversation_upsert'),
    squadId: SquadIdSchema,
    column: CardColumnIdSchema,
    card: ConversationBoardCardSchema,
  }),
  z.object({
    kind: z.literal('conversation_remove'),
    squadId: SquadIdSchema,
    cardId: CardIdSchema,
  }),
  z.object({
    kind: z.literal('task_upsert'),
    squadId: SquadIdSchema,
    column: TaskColumnIdSchema,
    task: TaskWithCardSchema,
  }),
  z.object({
    kind: z.literal('task_remove'),
    squadId: SquadIdSchema,
    taskId: TaskIdSchema,
  }),
  // Triage inbox is org-flat (no squad): untriaged cards awaiting routing.
  z.object({
    kind: z.literal('triage_upsert'),
    card: TriageCardRowSchema,
  }),
  z.object({
    kind: z.literal('triage_remove'),
    cardId: CardIdSchema,
  }),
]);
export type CardPushItem = z.infer<typeof CardPushItemSchema>;

/** One SSE `data:` frame — a coalesced batch of deltas. */
export const CardPushEnvelopeSchema = z.object({
  v: z.literal(1),
  items: z.array(CardPushItemSchema),
});
export type CardPushEnvelope = z.infer<typeof CardPushEnvelopeSchema>;

/**
 * First SSE frame after connect (sent as the `hello` event): the connection's
 * resolved agent id, or null when the user has no agent in the org. Lets the
 * client split its own "my tasks" from the board columns on task pushes.
 */
export const SseHelloSchema = z.object({ agentId: AgentIdSchema.nullable() });
export type SseHello = z.infer<typeof SseHelloSchema>;
